import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useData as useVikeData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';

import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItemLabel,
  MenuPopover,
} from '@/components/react-aria/menu';
import { Switch } from '@/components/react-aria/switch';
import { Tab, TabList, Tabs } from '@/components/react-aria/tabs';
import { NonFousableTooltipTarget, Tooltip, TooltipTrigger } from '@/components/react-aria/tooltip';
import Header from '@/renderer/Header';
import { useLocale } from '@/renderer/utils/locale';

import type { ResultsFormFilters } from './FilterTypes';
import { filterUrl } from './consts';

const sportFilters: {
  text: string;
  // Optional shorter label used in the page H1 (the dropdown uses `text`).
  title?: string;
  subtitle?: string;
  value: string;
  url: string;
}[] = [
  {
    text: 'RAS Service Sports',
    subtitle: 'Includes NFL, college football and college basketball',
    value: 'ras',
    url: 'ras',
  },
  {
    text: 'NFL & CFB since 2020',
    title: 'NFL + CFB',
    subtitle: 'NFL + college football combined',
    value: 'nfl,cfb',
    url: 'nfl,cfb',
  },
  { text: 'NFL', value: '3', url: 'nfl' },
  { text: 'College Football', value: '1', url: 'cfb' },
  { text: 'College Basketball', value: '2', url: 'cbb' },
  { text: 'All Sports', value: '0', url: 'all' },
];

const firstYear = (str?: string) => str?.split?.('-')?.[0] || '?';
const lastYear = (str?: string) => {
  const split = str?.split?.('-');
  return split?.[1] || split?.[0] || '?';
};

// The public SEO landings carry their own <title> from +config.ts; the computed
// "<sport> results since <date>" title is for the in-app toggle routes only.
const KEEP_OWN_TITLE = ['/nfl-results', '/cfb-archive'];

export default function Filters({
  baseUrl,
  form,
  hide = false,
  hideHeader = false,
  title,
  sportId,
  seasonId,
}: {
  form: UseFormReturn<ResultsFormFilters>;
  hide?: boolean;
  hideHeader?: boolean;
  title: string;
  baseUrl: string;
  sportId?: string;
  seasonId?: string;
}) {
  const data = useVikeData() as {
    seasons: API.Season[];
    seasonsSimple: {
      last5: API.Season[];
      first: API.Season;
      last: API.Season;
      selected: API.Season[];
    };
  };

  const seasonSelection = data.seasonsSimple;

  const { formatDate } = useLocale();

  // A custom ?startDate=yyyyMMdd filter (e.g. the home "verified record" link to
  // NFL+CFB since betstamp tracking began) drives a date range that isn't a season,
  // so the season-derived title would be wrong. Parse it to override the title.
  const pageContext = usePageContext();
  const searchStartDate = (pageContext.urlParsed?.search as Record<string, string> | undefined)
    ?.startDate;
  const customStartDate =
    searchStartDate && /^\d{8}$/.test(searchStartDate)
      ? new Date(
          +searchStartDate.slice(0, 4),
          +searchStartDate.slice(4, 6) - 1,
          +searchStartDate.slice(6, 8),
        )
      : null;

  const seasonFilters = useMemo(() => {
    const nowYear = new Date().getUTCFullYear();
    // The "current season" title must reflect the SAME date range the results query
    // uses (ResultsWrapper derives startDate from seasonsSimple.selected), NOT the
    // newest season (seasonsSimple.last). They diverge when a brand-new, still-empty
    // season exists — e.g. a July-1 futures season pushes `last` to a date the shown
    // plays don't cover ("since July 1, 2026" over last year's data). Use `selected`.
    const currentSeasonStart =
      seasonSelection?.selected?.[(seasonSelection?.selected?.length || 1) - 1]?.startDate ||
      seasonSelection?.last?.startDate;
    const base = [
      {
        id: 'current',
        description: 'Current season',
        str: `$sport results since ${(currentSeasonStart ? formatDate(currentSeasonStart, { weekday: undefined, year: 'numeric', month: 'long', day: 'numeric' }) : '') || '-'}`,
        years: `${nowYear}`,
        disabled: false,
      },
      {
        id: 'last',
        description: 'Last season',
        str: '$sport results for last season',
        years: `${nowYear - 1}`,
        disabled: false,
      },
      {
        id: 'last-2',
        description: 'Last 2 seasons',
        str: '$sport results for the last 2 seasons',
        years: `${nowYear - 1}-${nowYear}`,
        disabled: false,
      },
      {
        id: 'last-3',
        description: 'Last 3 seasons',
        str: '$sport results for the last 3 seasons',
        years: `${nowYear - 2}-${nowYear}`,
        disabled: false,
      },
      {
        id: 'last-5',
        description: 'Last 5 seasons',
        str: '$sport results for the last 5 seasons',
        years: `${nowYear - 3}-${nowYear}`,
        disabled: false,
      },
      {
        id: 'all-time',
        description: 'All-time',
        str: '$sport all-time results',
        years: `--${nowYear}`,
        disabled: false,
      },
    ];

    if (seasonSelection) {
      const isRasPlusSeasons =
        /^([A-Za-z]+)\/([A-Za-z]+) /.test(seasonSelection.first?.name) &&
        /^([A-Za-z]+)\/([A-Za-z]+) /.test(seasonSelection.last?.name);

      if (isRasPlusSeasons) {
        // Keep Fall/Winter and Spring/Summer to name if it is there per RASPLUS_SEASONS
        base[0].years = seasonSelection.last?.name || '?';
        base[1].years = seasonSelection.last5[1]?.name || '?';
        base[2].years = `${seasonSelection.last5[1]?.name.split('/')[0]} ${firstYear(seasonSelection.last5[1]?.name.split(' ')[1])}-${seasonSelection.last5[0]?.name.match(/^[A-Za-z]+\/([A-Za-z]+) /)?.[1]} ${lastYear(seasonSelection.last5[0]?.name)}`;
        base[3].years = `${seasonSelection.last5[2]?.name.split('/')[0]} ${firstYear(seasonSelection.last5[2]?.name.split(' ')[1])}-${seasonSelection.last5[0]?.name.match(/^[A-Za-z]+\/([A-Za-z]+) /)?.[1]} ${lastYear(seasonSelection.last5[0]?.name)}`;
        base[4].years = `${seasonSelection.last5[4]?.name.split('/')[0]} ${firstYear(seasonSelection.last5[4]?.name.split(' ')[1])}-${seasonSelection.last5[0]?.name.match(/^[A-Za-z]+\/([A-Za-z]+) /)?.[1]} ${lastYear(seasonSelection.last5[0]?.name)}`;
        base[5].years = `${seasonSelection.first?.name.split('/')[0]} ${firstYear(seasonSelection.first?.name.split(' ')[1])}-${seasonSelection.last?.name.match(/^[A-Za-z]+\/([A-Za-z]+) /)?.[1]} ${lastYear(seasonSelection.last?.name)}`;
      } else {
        base[0].years = seasonSelection.last?.name || '?';
        base[1].years = seasonSelection.last5[1]?.name || '?';
        base[2].years = `${firstYear(seasonSelection.last5[1]?.name)}-${lastYear(seasonSelection.last5[0]?.name)}`;
        base[3].years = `${firstYear(seasonSelection.last5[2]?.name)}-${lastYear(seasonSelection.last5[0]?.name)}`;
        base[4].years = `${firstYear(seasonSelection.last5[4]?.name)}-${lastYear(seasonSelection.last5[0]?.name)}`;
        const finalYear = lastYear(seasonSelection.last?.name);
        base[5].years = `${firstYear(seasonSelection.first?.name)}-${finalYear.length === 2 ? `20${finalYear}` : finalYear}`;
      }

      if (!seasonSelection.last5[4]) {
        // base.splice(4, 1);
        base[4].disabled = true;
      }
      if (!seasonSelection.last5[2]) {
        // base.splice(3, 1);
        base[3].disabled = true;
      }
      if (!seasonSelection.last5[1]) {
        // base.splice(1, 2);
        base[2].disabled = true;
        base[1].disabled = true;
      }
      if (!seasonSelection.last5[0]) {
        // base.splice(1, 1);
        base[1].disabled = true;
        base[0].years = `--`;
      }
    }

    return base;
  }, [seasonSelection, formatDate]);

  const season =
    (!!seasonId && data?.seasons?.find?.((season) => `${season.id}` === `${seasonId}`)) ||
    undefined;
  const seasonName = season?.name || '';

  const [pageTitle, pageSubtitle] = useMemo(() => {
    const prefix = (title === 'My Results' && 'My') || '';

    const foundSport = sportFilters.find((sport) => `${sportId || 'ras'}` === sport.value);

    const sportTitle =
      sportId === '0' || sportId === 'all'
        ? prefix
          ? 'all sports'
          : 'All sports'
        : foundSport?.title || foundSport?.text || `${sportId || ''}`.toUpperCase() || '';

    if (customStartDate) {
      const since = formatDate(customStartDate, {
        weekday: undefined,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return [`${prefix} ${sportTitle} results since ${since}`.trim(), foundSport?.subtitle];
    }

    const curSeasonId = seasonId || 'current';
    const seasonTitle =
      seasonFilters
        .find((filterer) => filterer.id === curSeasonId)
        ?.str?.replace?.('$sport', sportTitle) ||
      (seasonSelection.selected.length === 1
        ? `${seasonSelection.selected[0].name} ${sportTitle} results`
        : '') ||
      '';

    return [`${prefix} ${seasonTitle || `${sportTitle} results`}`.trim(), foundSport?.subtitle];
  }, [title, seasonFilters.find, seasonId, sportId, customStartDate, formatDate]);

  return (
    <>
      {!hideHeader && (
        <Header
          title={title}
          pageTitle={KEEP_OWN_TITLE.includes(baseUrl) ? undefined : pageTitle}
          className={twMerge('py-2', !!hide && 'md:hidden')}
        />
      )}
      {!hide && (
        <div className="mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
          <h1 className="mb-4 block text-2xl font-semibold text-zinc-900 max-sm:text-base dark:text-zinc-100">
            {pageTitle}
            {!!pageSubtitle && (
              <>
                {' '}
                <TooltipTrigger delay={1000}>
                  <NonFousableTooltipTarget>
                    <InformationCircleIcon
                      height={20}
                      className="inline-block size-5 max-sm:size-4"
                    />
                  </NonFousableTooltipTarget>
                  <Tooltip>{pageSubtitle}</Tooltip>
                </TooltipTrigger>
              </>
            )}
          </h1>
          <div
            className={clsx(
              'flex flex-col items-center justify-between rounded-sm xl:flex-row',
              'border-zinc-900/10 dark:border-white/20',
              'xl:border xl:bg-zinc-50 xl:px-4 xl:py-2 xl:dark:bg-white/5',
            )}
          >
            <div
              className={clsx(
                'flex w-full grow flex-col rounded-sm max-xl:justify-between lg:flex-row',
                'divide-zinc-900/20 border-zinc-900/10 dark:border-white/20',
                'max-xl:border max-xl:bg-zinc-50 max-xl:px-4 max-xl:py-2 max-xl:dark:bg-white/5',
              )}
            >
              <div className="flex flex-row justify-between lg:my-auto lg:mr-4 lg:block">
                <SportsMenu
                  seasonId={seasonId}
                  sportId={sportId}
                  seasonName={seasonName}
                  baseUrl={baseUrl}
                />
              </div>
              <div className="flex flex-col items-start justify-center space-x-1 text-xs">
                <SeasonTabs
                  seasonFilters={seasonFilters}
                  seasonId={seasonId}
                  sportId={sportId}
                  seasonName={seasonName}
                  baseUrl={baseUrl}
                />
              </div>
            </div>
            <Controller
              control={form.control}
              name="hidePropsFutures"
              render={({ field: { onChange } }) => (
                <Switch
                  labelPosition="right"
                  className="min-w-fit grow text-xs text-zinc-700 max-xl:mt-4 max-xl:self-end dark:text-zinc-300"
                  plain
                  {...form.register('hidePropsFutures')}
                  onChange={(isSelected) => onChange(isSelected)}
                  defaultSelected={form.watch('hidePropsFutures')}
                >
                  Hide props & futures
                </Switch>
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}

function SportsMenu({
  baseUrl,
  sportId,
  seasonId,
  seasonName,
}: {
  baseUrl: string;
  sportId?: string;
  seasonId?: string;
  seasonName?: string;
}) {
  const seasonIdIsNumber =
    !!seasonId && (typeof seasonId === 'number' || /^[0-9]+$/.test(seasonId));
  return (
    <>
      <MenuTrigger>
        <MenuButton
          variant="unstyle"
          className="script-only hover:border-border flex w-max items-center rounded-xl border border-transparent py-1 ps-1 pe-2 text-sm hover:border [&_svg]:size-4"
          aria-label="Sport"
        >
          {sportFilters.find(
            (item) => `${item.value}` === `${sportId || 'ras'}` || `${item.url}` === `${sportId}`,
          )?.text || 'Sport'}
        </MenuButton>
        <MenuPopover>
          <Menu>
            {sportFilters.map((sport) => (
              <MenuItem
                href={filterUrl(
                  baseUrl,
                  sport.url,
                  seasonIdIsNumber ? undefined : seasonId,
                  seasonName,
                )}
                key={sport.value}
                id={sport.value}
                className="cursor-pointer text-inherit"
              >
                <MenuItemLabel>{sport.text}</MenuItemLabel>
              </MenuItem>
            ))}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
      <noscript>
        <Tabs variant="pills" className="w-min max-sm:hidden" selectedKey={sportId || 'ras'}>
          <TabList className="flex-wrap space-x-2">
            {sportFilters.map((sport) => {
              return (
                <Tab
                  id={sport.value}
                  key={sport.value}
                  href={filterUrl(
                    baseUrl,
                    sport.url || sport.value,
                    seasonIdIsNumber ? undefined : '',
                  )}
                  className={twMerge(
                    'ml-0! w-max text-xs/4!',
                    'rounded-sm px-1.5 py-1 min-[815px]:px-2.5',
                    'group/tab selected:bg-zinc-600 dark:selected:bg-emerald-300/30 dark:selected:shadow-inner hover:bg-zinc-500 dark:hover:bg-emerald-300/20',
                  )}
                >
                  <span className="group-selected/tab:text-white group-selected/tab:dark:text-emerald-50 text-zinc-900 group-hover/tab:text-white dark:text-white">
                    <span className="text-xs font-medium text-inherit group-hover/tab:text-inherit dark:text-inherit dark:group-hover/tab:text-inherit">
                      {sport.text}
                    </span>
                  </span>
                </Tab>
              );
            })}
          </TabList>
        </Tabs>
      </noscript>
    </>
  );
}

function SeasonTabs({
  seasonFilters,
  baseUrl,
  sportId,
  seasonId,
  seasonName,
}: {
  seasonFilters: { id: string; description: string; years: string; disabled: boolean }[];
  baseUrl: string;
  sportId?: string;
  seasonId?: string;
  seasonName?: string;
}) {
  return (
    <>
      <MenuTrigger>
        <MenuButton
          variant="unstyle"
          className="hover:border-border flex w-max items-center rounded-xl border border-transparent py-1 ps-1 pe-2 text-sm hover:border sm:hidden [&_svg]:size-4"
          aria-label="Season"
        >
          {seasonFilters.find((item) => `${item.id}` === `${seasonId}`)?.description || 'Season'}
        </MenuButton>
        <MenuPopover>
          <Menu>
            {seasonFilters.map(
              ({ years, description, id, disabled }) =>
                !disabled && (
                  <MenuItem
                    href={filterUrl(baseUrl, sportId, id, '')}
                    key={id}
                    id={id}
                    className="cursor-pointer text-inherit"
                  >
                    <MenuItemLabel>{description}</MenuItemLabel>
                  </MenuItem>
                ),
            )}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
      <Tabs variant="pills" className="max-sm:hidden" selectedKey={seasonId || 'current'}>
        <TabList className="space-x-2">
          {seasonFilters.map(({ years, description, id, disabled }) => {
            return (
              !disabled && (
                <Tab
                  isDisabled={disabled}
                  id={id}
                  key={id}
                  href={filterUrl(baseUrl, sportId, id, '')}
                  className={twMerge(
                    'w-max text-xs/4!',
                    'rounded-sm px-1.5 py-1 min-[815px]:px-2.5',
                    'group/tab selected:bg-zinc-600 dark:selected:bg-emerald-300/30 dark:selected:shadow-inner hover:bg-zinc-500 dark:hover:bg-emerald-300/20',
                  )}
                >
                  <span className="group-selected/tab:text-white group-selected/tab:dark:text-emerald-50 text-zinc-900 group-hover/tab:text-white dark:text-white">
                    <span className="text-xs font-medium text-inherit group-hover/tab:text-inherit dark:text-inherit dark:group-hover/tab:text-inherit">
                      {description?.replace?.(/ seasons?/, '') || ''}
                    </span>
                    <span
                      className={twMerge(
                        'text-[11px] text-inherit group-hover/tab:text-inherit dark:text-inherit dark:group-hover/tab:text-inherit',
                        'group-selected/tab:text-inherit group-selected/tab:dark:text-inherit block',
                      )}
                    >
                      {years}
                    </span>
                  </span>
                </Tab>
              )
            );
          })}
        </TabList>
      </Tabs>
    </>
  );
}
